import { JMCButton } from "@components/JMCButton/JMCButton";
import { JMCHtml } from "@components/JMCHtml/JMCHtml";
import {
    Color,
    HeaderHero,
    Size,
    Variant,
} from "@jmc/solid-design-system/src/components/molecules/HeaderHero/HeaderHero";
import { HEADING_BACKGROUNDS } from "@jmc/solid-design-system/src/components/molecules/Heading/Heading";
import { CMSButtonProps, CMSFootnotes, CMSHeroProps } from "@types";
import { default as cmsMapper } from "@utils/cms-mapper";
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image";
import React from "react";

interface Props {
    id?: string;
    hero?: CMSHeroProps;
    footnotes?: CMSFootnotes[];
    placement?: string;
    variant?: Variant;
    color?: Color | HEADING_BACKGROUNDS;
    sidesColor?: Color;
    height?: "Extra Small" | "Small" | "Large" | "Medium" | "auto";
    title?: JSX.Element;
}

export const JMCHeaderHero: React.FunctionComponent<Props> = ({
    hero,
    footnotes,
    placement,
    id = undefined,
    variant,
    color,
    height,
    title,
    sidesColor,
}: Props) => {
    const { ...heroProps } = cmsMapper.mapHeaderHeroProps(hero);

    let size: Size = undefined;
    switch (height) {
        case "Extra Small":
            size = Size["X-Small"];
            break;
        case "Small":
            size = Size.Small;
            break;
        case "Medium":
            size = Size.Medium;
            break;
        case "Large":
            size = Size.Large;
            break;
    }

    const altTitle = title
        ? title?.props?.children
        : typeof hero?.display_title === "string"
          ? hero?.display_title
          : "";

    const buttonColor = color === Color.PRIMARY ? "White" : "Primary Color";
    const actions = hero?.call_to_action?.map(({ button }: { button: CMSButtonProps }, index: number) => {
        const buttonProps = {
            ...button,
            color: buttonColor,
            variant: index === 0 ? "Contained" : "Naked",
            flexibleWidth: index === 0 ? false : true,
        } as CMSButtonProps;

        return <JMCButton key={index} button={buttonProps} footnotes={footnotes} placement={placement} size="large" />;
    });

    const content = hero?.hero_body ? (
        <div>
            <JMCHtml footnotes={footnotes} color="inherit" size="inherit" placement={placement}>
                {hero.hero_body}
            </JMCHtml>
        </div>
    ) : null;

    const titleComponent = title ? (
        title
    ) : (
        <div>
            {typeof hero?.display_title === "string" ? (
                <JMCHtml footnotes={footnotes}>{hero.display_title}</JMCHtml>
            ) : (
                hero?.display_title
            )}
        </div>
    );

    // TODO: Remove after rebranding as it does not have effect in the image
    let aspectRatio = undefined;
    if (variant === Variant.MEDIA_SMALL && heroProps?.image) {
        aspectRatio = 4 / 3;
        if (typeof heroProps?.image !== "string") {
            heroProps.image.height = 0.75;
        }
    }

    // We render SVGs using IMG tags instead of GatsbyImages because there's only the URL in the graphql query
    // For SVGs, there's no GatsbyImageData in the graphql query, so we need to use IMG tags
    let heroImage: JSX.Element = null;
    if (heroProps?.image) {
        if (typeof heroProps?.image === "string") {
            heroImage = (
                <img
                    src={heroProps?.image}
                    alt={altTitle || ""}
                    loading="eager"
                    style={{
                        objectPosition: heroProps.imagePosition.replace("-", " "),
                        objectFit: "cover",
                        aspectRatio,
                    }}
                ></img>
            );
        } else {
            heroImage = (
                <GatsbyImage
                    image={heroProps?.image as unknown as IGatsbyImageData}
                    alt={altTitle || ""}
                    loading="eager"
                    imgStyle={{ objectPosition: heroProps.imagePosition.replace("-", " "), aspectRatio }}
                />
            );
        }
    }

    return (
        <HeaderHero
            id={id || heroProps.id}
            title={typeof hero?.display_title === "string" ? hero?.display_title : ""}
            titleComponent={title || titleComponent}
            content={content}
            actions={actions}
            image={heroImage}
            size={size || heroProps.size || undefined}
            variant={variant}
            color={color === "transparent" ? Color.DEFAULT : (color as Color)}
            sidesColor={sidesColor}
        />
    );
};
